import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { Switch } from '../../ui/switch';
import { supabase } from '../../../lib/supabaseClient';
import { Mail, Loader2, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';

const EmailSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    smtp_host: '',
    smtp_port: '587',
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    smtp_use_tls: true,
    smtp_enabled: false,
  });

  useEffect(() => {
    fetchEmailSettings();
  }, []);

  const fetchEmailSettings = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setError('Not authenticated');
        return;
      }

      // Fetch email settings from user_roles or a new email_settings table
      // For now, we'll use localStorage as a placeholder
      // TODO: Create email_settings table in database
      const savedSettings = localStorage.getItem(`email_settings_${user.id}`);

      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setFormData(settings);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching email settings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setSuccess(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      // Validate required fields if SMTP is enabled
      if (formData.smtp_enabled) {
        if (!formData.smtp_host || !formData.smtp_port || !formData.smtp_username ||
            !formData.smtp_password || !formData.smtp_from_email) {
          setError('Please fill in all required fields');
          return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.smtp_from_email)) {
          setError('Please enter a valid email address');
          return;
        }

        // Validate port is a number
        const port = parseInt(formData.smtp_port);
        if (isNaN(port) || port < 1 || port > 65535) {
          setError('Please enter a valid port number (1-65535)');
          return;
        }
      }

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setError('Not authenticated');
        return;
      }

      // TODO: Save to database table instead of localStorage
      // For now, using localStorage as a placeholder
      localStorage.setItem(`email_settings_${user.id}`, JSON.stringify(formData));

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving email settings:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    // TODO: Implement test email sending
    alert('Test connection feature coming soon. This will send a test email to verify your SMTP settings.');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            <CardTitle>Email Settings</CardTitle>
          </div>
          <CardDescription>
            Configure your SMTP settings to send emails directly from the platform when responding to client requests.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Error/Success Messages */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="border-green-500 text-green-700">
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>Email settings saved successfully!</AlertDescription>
            </Alert>
          )}

          {/* Enable SMTP Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/50">
            <div className="space-y-0.5">
              <Label htmlFor="smtp-enabled" className="text-base font-semibold">
                Enable SMTP Email
              </Label>
              <p className="text-sm text-muted-foreground">
                Allow sending emails via your configured SMTP server
              </p>
            </div>
            <Switch
              id="smtp-enabled"
              checked={formData.smtp_enabled}
              onCheckedChange={(checked) => handleInputChange('smtp_enabled', checked)}
            />
          </div>

          {/* SMTP Configuration Fields */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtp_host">
                  SMTP Host *
                </Label>
                <Input
                  id="smtp_host"
                  type="text"
                  placeholder="smtp.gmail.com"
                  value={formData.smtp_host}
                  onChange={(e) => handleInputChange('smtp_host', e.target.value)}
                  disabled={!formData.smtp_enabled}
                />
                <p className="text-xs text-muted-foreground">
                  Example: smtp.gmail.com, smtp.office365.com
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="smtp_port">
                  SMTP Port *
                </Label>
                <Input
                  id="smtp_port"
                  type="number"
                  placeholder="587"
                  value={formData.smtp_port}
                  onChange={(e) => handleInputChange('smtp_port', e.target.value)}
                  disabled={!formData.smtp_enabled}
                />
                <p className="text-xs text-muted-foreground">
                  Common: 587 (TLS), 465 (SSL), 25
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="smtp_username">
                SMTP Username/Email *
              </Label>
              <Input
                id="smtp_username"
                type="text"
                placeholder="your-email@example.com"
                value={formData.smtp_username}
                onChange={(e) => handleInputChange('smtp_username', e.target.value)}
                disabled={!formData.smtp_enabled}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="smtp_password">
                SMTP Password/App Password *
              </Label>
              <div className="relative">
                <Input
                  id="smtp_password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••••••"
                  value={formData.smtp_password}
                  onChange={(e) => handleInputChange('smtp_password', e.target.value)}
                  disabled={!formData.smtp_enabled}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  disabled={!formData.smtp_enabled}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                For Gmail, use an App Password. For Office 365, use your account password.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="smtp_from_email">
                From Email Address *
              </Label>
              <Input
                id="smtp_from_email"
                type="email"
                placeholder="your-email@example.com"
                value={formData.smtp_from_email}
                onChange={(e) => handleInputChange('smtp_from_email', e.target.value)}
                disabled={!formData.smtp_enabled}
              />
              <p className="text-xs text-muted-foreground">
                This email address will appear in the "From" field
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="smtp_from_name">
                From Name (Optional)
              </Label>
              <Input
                id="smtp_from_name"
                type="text"
                placeholder="Appsytec Recruitment"
                value={formData.smtp_from_name}
                onChange={(e) => handleInputChange('smtp_from_name', e.target.value)}
                disabled={!formData.smtp_enabled}
              />
              <p className="text-xs text-muted-foreground">
                Display name for outgoing emails
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="smtp_use_tls"
                checked={formData.smtp_use_tls}
                onCheckedChange={(checked) => handleInputChange('smtp_use_tls', checked)}
                disabled={!formData.smtp_enabled}
              />
              <Label htmlFor="smtp_use_tls" className="font-normal cursor-pointer">
                Use TLS/STARTTLS encryption (recommended)
              </Label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleSave}
              disabled={saving || !formData.smtp_enabled}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Save Settings
                </>
              )}
            </Button>

            <Button
              variant="outline"
              onClick={handleTestConnection}
              disabled={!formData.smtp_enabled || saving}
            >
              <Mail className="mr-2 h-4 w-4" />
              Test Connection
            </Button>
          </div>

          {/* Help Text */}
          <Alert>
            <Mail className="h-4 w-4" />
            <AlertDescription className="text-xs">
              <strong>Note:</strong> These settings allow you to send emails directly through your own email provider.
              <br />
              <br />
              <strong>Gmail users:</strong> You'll need to create an App Password in your Google Account settings.
              <br />
              <strong>Office 365 users:</strong> You can use your regular account password, but ensure SMTP is enabled for your account.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailSettings;
